// The campaign's "Create Board Ask" (EMC-WEB4, frame 21), at `/fundraisers/$fundraiserId/board-ask`:
// an admin with Fundraisers access writes the ask to the board from the campaign. Board owns the ask
// (plan §2.1): this page calls Board's org-door operations for the campaign
// (`/board/{org_ref}/campaigns/{fundraiser_id}/ask`), which admit a Fundraisers writer.
//
// D-EMC-15 as ruled 2026-10-31 23:00: "An admin with access to the Board module needs to approve the
// board ask before it goes out." A drafter without Board access saves and submits it ("Submit for
// approval"); a drafter who has Board access approves and sends it here ("Approve and send").

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { formatMoneyDisplay } from '@givmo/api-client';
import { Button, Card, CardBody, ErrorMessage, PageHeader, testIdProp } from '@givmo/design-system';
import {
  BOARD_ASK_COPY as w,
  BoardAskForm,
  BoardAskRecipientsCard,
  boardAskDraftBody,
  boardAskKeys,
  boardAskRefusal,
  fetchBoardAskForCampaign,
  isBoardAskUnavailable,
  organizationDate,
  saveBoardAskDraft,
  sendBoardAsk,
  submitBoardAsk,
  type BoardAsk,
  type BoardAskCampaign,
  type BoardAskFormValue,
  type BoardAskRecipient,
} from '@givmo/org-console';
import { defaultZone } from './emails-decode';
import { useFundraisersModule } from './fundraisers-context';
import { fundraiserOptions } from './fundraisers-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import {
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
} from './fundraisers-state';

function goalLabel(goal: unknown): string | null {
  if (goal === null || goal === undefined) return null;
  const shown = formatMoneyDisplay(String(goal), { cents: 'when-present' });
  return shown === null ? null : `$${shown}`;
}

/** "Thu, Oct 29, 1:12 PM": when it was submitted, on the reader's clock. */
function submittedDay(instant: string | null): string {
  const date = new Date(instant ?? '');
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function valueOf(ask: BoardAsk | null, pagesOpen: boolean): BoardAskFormValue {
  const gone = ask?.state === 'gone';
  return {
    message: ask?.message ?? '',
    attachments: ask?.attachments ?? [],
    offersShare: ask?.offersShare ?? true,
    // What the campaign can't take is never offered; an ask that went keeps what it said.
    offersPage: gone ? (ask?.offersPage ?? true) : pagesOpen && (ask?.offersPage ?? true),
  };
}

export function BoardAskPage({ fundraiserId }: { fundraiserId: string }) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const enabled = config.orgRef !== null;
  const ask = useQuery({
    queryKey: boardAskKeys.campaign(orgRef, fundraiserId),
    queryFn: ({ signal }) => fetchBoardAskForCampaign(orgRef, fundraiserId, signal),
    enabled,
    retry: false,
  });
  const fundraiser = useQuery({ ...fundraiserOptions(orgRef, fundraiserId), enabled });
  if (ask.isError) {
    return (
      <FundraisersPageShell testId="page-board-ask">
        {isBoardAskUnavailable(ask.error) ? (
          <p className="text-s14 text-text-secondary">{w.unavailable}</p>
        ) : (
          <FundraisersReadState error={ask.error} onRetry={() => void ask.refetch()} />
        )}
      </FundraisersPageShell>
    );
  }
  if (ask.isPending) {
    return (
      <FundraisersPageShell testId="page-board-ask">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  }
  const loaded = ask.data;
  return (
    <LoadedAsk
      // A new answer from the server starts the form from what it holds.
      key={`${loaded.ask?.id ?? 'new'}:${loaded.ask?.state ?? ''}`}
      fundraiserId={fundraiserId}
      campaignName={fundraiser.data?.name ?? loaded.ask?.campaignName ?? ''}
      goal={goalLabel(fundraiser.data?.goal_amount)}
      ask={loaded.ask}
      campaign={loaded.campaign}
      recipients={loaded.ask?.state === 'gone' ? loaded.ask.recipients : loaded.recipients}
      canApprove={loaded.canApprove}
    />
  );
}

function LoadedAsk({
  fundraiserId,
  campaignName,
  goal,
  ask,
  campaign,
  recipients,
  canApprove,
}: {
  fundraiserId: string;
  campaignName: string;
  goal: string | null;
  ask: BoardAsk | null;
  campaign: BoardAskCampaign | null;
  recipients: BoardAskRecipient[];
  canApprove: boolean;
}) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const client = useQueryClient();
  // Core unread, the server still refuses at the act; the page doesn't guess.
  const live = campaign === null || campaign.status === 'Active';
  const pagesOpen = campaign === null || campaign.acceptsPages;
  const [value, setValue] = useState<BoardAskFormValue>(() => valueOf(ask, pagesOpen));
  const [error, setError] = useState<string | null>(null);
  const gone = ask?.state === 'gone';
  const waiting = ask?.state === 'submitted';
  const ready = value.message.trim().length > 0 && (value.offersShare || value.offersPage) && live;
  const back = FUNDRAISERS_ROUTES.editor(fundraiserId);
  const refresh = () =>
    client.invalidateQueries({ queryKey: boardAskKeys.campaign(orgRef, fundraiserId) });

  const go = useMutation({
    mutationFn: async (then: 'save' | 'submit' | 'send') => {
      const saved = await saveBoardAskDraft(orgRef, fundraiserId, boardAskDraftBody(value));
      if (then === 'submit') return submitBoardAsk(orgRef, fundraiserId);
      if (then === 'send') return sendBoardAsk(orgRef, saved.id);
      return saved;
    },
    onMutate: () => setError(null),
    onSuccess: () => refresh(),
    onError: (failure) => setError(boardAskRefusal(failure, w.saveFailed)),
  });

  return (
    <FundraisersPageShell testId="page-board-ask">
      <div>
        <a
          href={back}
          className="inline-flex items-center gap-8 text-s14 font-w600 text-text-strong hover:underline"
          onClick={(event) => {
            event.preventDefault();
            config.onNavigate(back);
          }}
          {...testIdProp('board-ask-back')}
        >
          <ArrowLeft aria-hidden className="size-16" />
          {campaignName}
        </a>
      </div>
      <PageHeader title={w.create} subtitle={w.createLede(config.orgName)} />
      {ask?.returnedAt && ask.state === 'draft' && (
        <p className="text-s14 text-text-secondary" {...testIdProp('board-ask-returned')}>
          {w.sentBack(ask.returnedByDisplayName)}
        </p>
      )}
      <div className="grid min-w-0 grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_300px]">
        <BoardAskForm
          orgRef={orgRef}
          orgName={config.orgName}
          value={value}
          onChange={setValue}
          goalLabel={goal}
          editable={!gone && !(waiting && !canApprove)}
          canUpload={config.permissions?.includes('profile:write') === true}
          pagesOpen={gone || pagesOpen}
          today={organizationDate(defaultZone(undefined, config.orgTimeZone))}
        />
        <div className="flex flex-col gap-16">
          <BoardAskRecipientsCard recipients={recipients} />
          <Card flush testId="board-ask-approval">
            <CardBody className="flex flex-col gap-14">
              {gone ? (
                <p className="text-s14 leading-h15 text-text-secondary">
                  {w.wentOut(
                    new Date(ask?.sentAt ?? '').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    }),
                  )}
                </p>
              ) : canApprove ? (
                <>
                  <p className="text-s14 leading-h15 text-text-secondary">
                    {live ? w.approvingSends : w.campaignNotLive}
                  </p>
                  <Button
                    appearance="saas"
                    tone="primary"
                    className="self-start"
                    disabled={go.isPending || recipients.length === 0 || !ready}
                    onClick={() => go.mutate('send')}
                    testId="board-ask-approve-and-send"
                  >
                    {w.approveAndSend}
                  </Button>
                </>
              ) : waiting ? (
                <div className="flex flex-col gap-4" {...testIdProp('board-ask-waiting')}>
                  <span className="text-s14 font-w600 text-text-strong">{w.submitted}</span>
                  <span className="text-s14 leading-h15 text-text-secondary">
                    {w.submittedWhen(submittedDay(ask?.submittedAt ?? null))}
                  </span>
                </div>
              ) : (
                <>
                  <p className="text-s14 leading-h15 text-text-secondary">
                    {live ? w.approvalNeeded : w.campaignNotLive}
                  </p>
                  <Button
                    appearance="saas"
                    tone="primary"
                    className="self-start"
                    disabled={go.isPending || !ready}
                    onClick={() => go.mutate('submit')}
                    testId="board-ask-submit"
                  >
                    {w.submit}
                  </Button>
                </>
              )}
              {!gone && !(waiting && !canApprove) && (
                <Button
                  appearance="saas"
                  tone="link"
                  size="sm"
                  className="self-start"
                  disabled={go.isPending}
                  onClick={() => go.mutate('save')}
                  testId="board-ask-save"
                >
                  {w.save}
                </Button>
              )}
              {error !== null && <ErrorMessage testId="board-ask-error">{error}</ErrorMessage>}
            </CardBody>
          </Card>
        </div>
      </div>
    </FundraisersPageShell>
  );
}
